import { Services } from '../../services'
import { EStorageKey } from '../../types/general'
import { CALLBACK_PATH, hint, safeReturnTo, SCOPE } from './paths'

/**
 * The browser's half of signing in through the SSO orchestrator, as
 * admin-center does it: OIDC Authorization Code + PKCE, the console a public
 * client (`Services.sso.client_id`). The `state`, nonce and verifier wait in
 * this tab's session storage between `/auth/login` and `/auth/callback`; the
 * server only fetches the discovery document (the orchestrator sends no CORS
 * header) and trades the code (`src/pages/api/auth`).
 */

// The `prompt` values the orchestrator passes on: `login` forces a fresh
// sign-in — the default, since Auth0's own session outlives the
// orchestrator's logout — and none (`?prompt=`) is plain SSO.
const PROMPTS = new Set(['login', 'none', 'consent', 'select_account'])

// How much life an id_token must have left to be sent as the logout hint:
// the orchestrator silently ignores an expired one.
const HINT_MARGIN_SECONDS = 30

type Discovery = {
  authorization_endpoint: string
  end_session_endpoint?: string
}

/** What a sign-in remembers between `/auth/login` and `/auth/callback`. */
export type LoginState = {
  state: string
  nonce: string
  verifier: string
  returnTo: string
  orgId?: string
}

let discovery: Promise<Discovery> | null = null

// The orchestrator's endpoints, through the console's own server.
function getDiscovery(): Promise<Discovery> {
  if (!discovery) {
    discovery = fetch('/api/auth/discovery')
      .then(async (res) => {
        const body = await res.json()

        if (!res.ok) {
          throw new Error(body?.error ?? 'The sign-in service is unavailable.')
        }

        return body as Discovery
      })
      .catch((err) => {
        // Never cached: the next attempt tries again.
        discovery = null
        throw err
      })
  }

  return discovery
}

/** Bytes as base64url, without padding. */
export function base64Url(bytes: Uint8Array): string {
  let text = ''

  bytes.forEach((byte) => {
    text += String.fromCharCode(byte)
  })

  return btoa(text).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

/** Random bytes, base64url: a `state`, a nonce or a PKCE verifier. */
export function randomString(byteLength = 32): string {
  const bytes = new Uint8Array(byteLength)

  crypto.getRandomValues(bytes)

  return base64Url(bytes)
}

/** The PKCE S256 challenge for a verifier. */
export async function challengeOf(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest(
    'SHA-256',
    new TextEncoder().encode(verifier)
  )

  return base64Url(new Uint8Array(digest))
}

/** The redirect URI registered with the orchestrator, on this origin. */
export function redirectUri(): string {
  return `${window.location.origin}${CALLBACK_PATH}`
}

/**
 * Starts a sign-in: a fresh `state`, nonce and verifier parked in this tab,
 * then the orchestrator's universal login, which signs in and signs up
 * alike. Afterwards the browser goes on to `returnTo`, in `orgId` when given.
 */
export async function beginLogin({
  returnTo,
  orgId,
  prompt,
  loginHint,
}: {
  returnTo?: string
  orgId?: string
  prompt?: string
  loginHint?: string
}) {
  const { authorization_endpoint } = await getDiscovery()

  const login: LoginState = {
    state: randomString(),
    nonce: randomString(),
    verifier: randomString(),
    returnTo: safeReturnTo(returnTo),
    orgId: hint(orgId),
  }

  sessionStorage.setItem(EStorageKey.LOGIN_STATE, JSON.stringify(login))

  const params: Record<string, string | undefined> = {
    response_type: 'code',
    client_id: Services.sso.client_id,
    redirect_uri: redirectUri(),
    scope: SCOPE,
    state: login.state,
    nonce: login.nonce,
    code_challenge: await challengeOf(login.verifier),
    code_challenge_method: 'S256',
    // Absent (or unknown): `login`. Present but empty: no `prompt`.
    prompt:
      prompt === ''
        ? undefined
        : prompt && PROMPTS.has(prompt)
          ? prompt
          : 'login',
    login_hint: hint(loginHint),
  }

  const url = new URL(authorization_endpoint)

  for (const [name, value] of Object.entries(params)) {
    if (value) {
      url.searchParams.set(name, value)
    }
  }

  window.location.assign(url.toString())
}

/**
 * The sign-in this tab started, checked against the `state` the
 * orchestrator sent back, and spent: it is never usable twice.
 */
export function consumeLoginState(returnedState: string | null): LoginState {
  const raw = sessionStorage.getItem(EStorageKey.LOGIN_STATE)

  sessionStorage.removeItem(EStorageKey.LOGIN_STATE)

  let login: LoginState | undefined

  try {
    login = raw ? JSON.parse(raw) : undefined
  } catch {
    login = undefined
  }

  if (
    !login?.state ||
    !login.verifier ||
    !login.nonce ||
    login.state !== returnedState
  ) {
    throw new Error(
      'The sign-in could not be verified: it was not started in this tab, or it was already used.'
    )
  }

  return login
}

/**
 * Ends the orchestrator's own session too (RP-initiated logout), back to
 * this origin, with the sign-in's id_token as the hint while it is fresh.
 * Answers false, without navigating, when the orchestrator offers no
 * logout; the caller then just reloads signed out.
 */
export async function beginLogout(idToken: string | null): Promise<boolean> {
  const { end_session_endpoint } = await getDiscovery()

  if (!end_session_endpoint) {
    return false
  }

  const url = new URL(end_session_endpoint)

  url.searchParams.set('client_id', Services.sso.client_id ?? '')
  url.searchParams.set('post_logout_redirect_uri', window.location.origin)

  if (idToken && expiresAt(idToken) - HINT_MARGIN_SECONDS > Date.now() / 1000) {
    url.searchParams.set('id_token_hint', idToken)
  }

  window.location.assign(url.toString())

  return true
}

// A token's `exp`, or 0 when it has none.
function expiresAt(token: string): number {
  try {
    const payload = token.split('.')[1] ?? ''
    const { exp } = JSON.parse(
      atob(payload.replace(/-/g, '+').replace(/_/g, '/'))
    )

    return typeof exp === 'number' ? exp : 0
  } catch {
    return 0
  }
}
